/** Flowers on grass, saplings and trees; and the extra trees that make a forest dense (trees.ts plants them). */
import { KIND, type Kind } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

import { hash2 } from '@core/math';

import type { PlanetView } from '../planet/view';
import type { Look } from '../style/look';
import { tileTop } from './geometry';
import type { Instances } from './instances';
import { plantBetween, plantSapling, plantTree } from './trees';

const FLOWER_COLORS = [0xf6d77a, 0xf2a7c3, 0xffffff, 0xc4b3f2].map((c) => new THREE.Color(c));

/** The plants of tile `i` at direction `c`; `flowers` per grass tile. Returns false for other kinds. */
export function placeFlora(
  ins: Instances,
  view: PlanetView,
  i: number,
  c: THREE.Vector3,
  kind: Kind,
  flowers: number,
  look: Look = 'classic',
): boolean {
  const r1 = hash2(i, 1);
  const r2 = hash2(i, 2);
  const r3 = hash2(i, 3);
  const yaw = r1 * 6.283;
  switch (kind) {
    case KIND.GRASS:
      for (let k = 0; k < flowers; k++) {
        const h1 = hash2(i, 20 + k);
        const h2 = hash2(i, 30 + k);
        ins.put(
          'flower',
          c,
          (h1 - 0.5) * 0.56,
          (h2 - 0.5) * 0.56,
          0,
          0.8 + h1 * 0.5,
          FLOWER_COLORS[Math.floor(hash2(i, 40 + k) * 4)],
        );
      }
      return true;
    case KIND.SAPLING:
      plantSapling(look, ins, c, yaw, r2);
      return true;
    case KIND.TREE: {
      const grouped = view.groups[i] === 1;
      plantTree(look, ins, c, yaw, 0.95 + r3 * 0.3, r2, grouped);
      if (r1 < 0.5 && !grouped) ins.put('bush', c, 0.27, 0.17, yaw, 0.62);
      return true;
    }
    default:
      return false;
  }
}

const _c = new THREE.Vector3();

/** Forests are dense: an extra tree where two forest tiles meet, and one where three do. */
export function fillForests(ins: Instances, view: PlanetView, look: Look = 'classic'): void {
  const { grid, kind: kinds, groups } = view;
  const forest = (i: number): boolean => kinds[i] === KIND.TREE && groups[i] === 1;
  const sum = (tiles: readonly number[]): THREE.Vector3 => {
    _c.set(0, 0, 0);
    for (const t of tiles) _c.x += grid.centers[t * 3] ?? 0;
    for (const t of tiles) _c.y += grid.centers[t * 3 + 1] ?? 0;
    for (const t of tiles) _c.z += grid.centers[t * 3 + 2] ?? 0;
    return _c.normalize();
  };
  for (let i = 0; i < grid.count; i++) {
    if (!forest(i)) continue;
    const nb = grid.neighbors[i];
    if (!nb) continue;
    for (const j of nb) {
      if (j < i || !forest(j)) continue;
      ins.at(-1, Math.min(tileTop(view, i), tileTop(view, j)) - 0.03);
      const hh = hash2(i * 7919 + j, 5);
      plantBetween(look, ins, sum([i, j]), hh * 6.28, 0.78 + hh * 0.25, hh, true);
      const nbj = grid.neighbors[j];
      if (!nbj) continue;
      for (const k of nb) {
        if (k <= j || !forest(k) || !nbj.includes(k)) continue;
        ins.at(-1, Math.min(tileTop(view, i), tileTop(view, j), tileTop(view, k)) - 0.03);
        const h3 = hash2(i * 31 + j * 7 + k, 9);
        plantBetween(look, ins, sum([i, j, k]), h3 * 6.28, 0.7 + h3 * 0.3, h3, false);
      }
    }
  }
}
